import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactStar from 'shared/design-system/components/TrueImpactStar';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import LoadMore from 'shared/hooks/LoadMore';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactReadmore from 'shared/design-system/components/TrueImpactReadmore';
import {
  getReportRatings,
  ReportRatingItem,
  ReportRatingsFilters,
  ReportRatingsListResponse,
} from './api';
import { formattedDateTime } from 'shared/utils/date';
import { numberWithCommas } from 'shared/utils/numberUtils';

const PER_PAGE = 12;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const ContentArea = styled.div`
  min-height: 400px;
  width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${SPACERS.medium};
  .rating-heading {
    margin: 0;
  }
  .total-badge {
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  flex-wrap: wrap;
  align-items: center;
  background: ${COLORS.grayLight};
  border-radius: 10px;
  padding: ${SPACERS.small};
  input,
  select {
    padding: 9px 14px;
    border: 1.5px solid ${COLORS.grayBase};
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    color: ${COLORS.blackBase};
    background: ${COLORS.whiteBase};
    transition: border-color 0.15s, box-shadow 0.15s;
    &:focus {
      border-color: ${COLORS.ctaPrimary};
      box-shadow: 0 0 0 3px ${COLORS.ctaPrimaryBackground};
    }
    &::placeholder {
      color: ${COLORS.grayDark};
    }
  }
  input {
    min-width: 220px;
    flex: 1;
  }
  select {
    min-width: 160px;
    cursor: pointer;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  gap: ${SPACERS.medium};
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  background: ${COLORS.whiteBase};
  border-radius: 12px;
  border: 1px solid ${COLORS.greyLight};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.06);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 115, 163, 0.13);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: ${SPACERS.small};
  background: ${COLORS.ctaPrimary};
  color: ${COLORS.whiteBase};
  .report-name {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .report-meta {
    display: flex;
    gap: ${SPACERS.extraTiny};
    flex-wrap: wrap;
    a,
    span {
      font-size: 11px;
      opacity: 0.9;
      background: rgba(255, 255, 255, 0.18);
      border-radius: 4px;
      padding: 2px 7px;
      letter-spacing: 0.2px;
    }
    a {
      color: inherit;
      text-decoration: none;
    }
  }
`;

const CardBody = styled.div`
  padding: ${SPACERS.small};
  flex: 1;
  .type-row {
    margin-bottom: ${SPACERS.extraTiny};
  }
  .rating-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
    .stars {
      display: flex;
      gap: 3px;
    }
    .rating-label {
      font-size: 13px;
      font-weight: 500;
      color: ${COLORS.grayDarkest};
    }
    .type-badge {
      margin-left: auto;
    }
  }
  .user-email {
    font-size: 13px;
    color: ${COLORS.grayDark};
    margin-bottom: ${SPACERS.extraTiny};
    a {
      color: ${COLORS.ctaPrimary};
      text-decoration: none;
      font-weight: 500;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  .description {
    margin: ${SPACERS.extraTiny} 0 ${SPACERS.tiny};
  }
  .meta-row {
    display: inline-flex;
    align-items: center;
    gap: ${SPACERS.extraTiny};
    margin-top: ${SPACERS.extraTiny};
  }
  .created-at {
    font-size: 12px;
    color: ${COLORS.grayBase};
  }
  .flag-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.3px;
    color: ${COLORS.success};
    background: ${COLORS.successBackground};
  }
  .flag-badge.cn {
    background: #b4c4ff;
    color: ${COLORS.grayDarkest};
  }
`;

const CardFooter = styled.div`
  padding: ${SPACERS.extraTiny} ${SPACERS.small} ${SPACERS.small};
  border-top: 1px solid ${COLORS.greyLight};
  background: ${COLORS.greyLightest};
  .investments-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: ${COLORS.grayDark};
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 6px;
    margin-top: ${SPACERS.extraTiny};
  }
  .investment-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    border-bottom: 1px solid ${COLORS.greyLight};
    &:last-child {
      border-bottom: none;
    }
    .funder-name {
      font-size: 13px;
    }
    .amount {
      font-size: 13px;
      font-weight: 600;
      color: ${COLORS.ctaBrand};
    }
  }
`;

const TypeBadge = styled.span<{ ratingType: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: capitalize;
  color: ${COLORS.ctaBrand};
  background: ${COLORS.ctaBrandBackground};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  color: ${COLORS.grayDark};
  background: ${COLORS.greyLightest};
  border-radius: 12px;
  border: 1px dashed ${COLORS.greyBase};
  .emoji {
    font-size: 48px;
    margin-bottom: ${SPACERS.small};
    display: block;
    opacity: 0.5;
  }
  p {
    font-size: 16px;
    margin: 0;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="stars">
    {Array.from({ length: 5 }, (_, i) => (
      <TrueImpactStar
        key={i}
        count={1}
        starColor={i < rating ? 'warning' : 'grayBase'}
        backgroundColor={i < rating ? 'warningBackground' : 'grayLightest'}
        filled={i < rating}
      />
    ))}
  </div>
);

const RatingCard: React.FC<{ item: ReportRatingItem }> = ({ item }) => {
  return (
    <Card>
      <CardHeader>
        <p className="report-name">{item.report?.name || 'Feedback'}</p>
        {item.report && (
          <TrueImpactText color="grayLight" className="report-meta">
            <a href={`/account/feedback/${item.report.id}`} target="_blank">
              Report ID: {item.report.id}
            </a>
            <span>{item.report.year}</span>
            <span>{item.report.stage}</span>
            <span>{item.report.status}</span>
          </TrueImpactText>
        )}
      </CardHeader>
      <CardBody>
        <div className="rating-row">
          <StarRating rating={item.rating} />
          <TrueImpactText className="rating-label">
            {item.rating} / 5
          </TrueImpactText>
          {item.type && (
            <TypeBadge
              ratingType={item.type.toLowerCase()}
              className="type-badge"
            >
              {item.type}
            </TypeBadge>
          )}
        </div>
        <div className="user-email">
          <a href={`mailto:${item.userEmail}`}>{item.userEmail}</a>
        </div>
        {item.description && (
          <TrueImpactReadmore
            text={item.description}
            lines={3}
            className="description"
          />
        )}
        <div className="meta-row">
          <TrueImpactText className="created-at">
            {formattedDateTime(item.createdAt)}
          </TrueImpactText>
          {item.report?.clonedFromId ? (
            <span className="flag-badge">Cloned</span>
          ) : null}
          {item.report?.cnOptedIn ? (
            <span className="flag-badge cn">Charity Navigator</span>
          ) : null}
        </div>
      </CardBody>
      {item.investments?.length > 0 && (
        <CardFooter>
          <div className="investments-label">Investment</div>
          {item.investments.map(inv => (
            <div key={inv.id} className="investment-item">
              <TrueImpactText className="funder-name">
                {inv.funder
                  ? inv.funder.name
                    ? `${inv.funder.name}${
                        inv.funder.email ? ` (${inv.funder.email})` : ''
                      }`
                    : inv.funder.email || ''
                  : 'Unknown Investor'}
              </TrueImpactText>
              <TrueImpactText className="amount">
                ${numberWithCommas(inv.allocationAmount ?? inv.total ?? 0)}
              </TrueImpactText>
            </div>
          ))}
        </CardFooter>
      )}
    </Card>
  );
};

const ReportRatings: React.FC = () => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<
    ReportRatingsListResponse
  >();

  const [items, setItems] = useState<ReportRatingItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [emailFilter, setEmailFilter] = useState('');
  const [reportIdFilter, setReportIdFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [hasClonedFromIdFilter, setHasClonedFromIdFilter] = useState('');
  const [cnOptedInFilter, setCnOptedInFilter] = useState('');

  const emailDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const reportIdDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const activeFilters: ReportRatingsFilters = useMemo(
    () => ({
      userEmail: emailFilter || undefined,
      reportId: reportIdFilter ? parseInt(reportIdFilter, 10) : undefined,
      rating: ratingFilter ? parseInt(ratingFilter, 10) : undefined,
      hasClonedFromId:
        hasClonedFromIdFilter === 'true'
          ? true
          : hasClonedFromIdFilter === 'false'
          ? false
          : undefined,
      hasCnOptedIn:
        cnOptedInFilter === 'true'
          ? true
          : cnOptedInFilter === 'false'
          ? false
          : undefined,
    }),
    [
      emailFilter,
      reportIdFilter,
      ratingFilter,
      hasClonedFromIdFilter,
      cnOptedInFilter,
    ],
  );

  const load = useCallback(
    (pageNum: number, filters: ReportRatingsFilters) => {
      if (!client) return;
      makeRequest(getReportRatings(client, pageNum, PER_PAGE, filters));
    },
    [client, makeRequest],
  );

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(false);
    load(1, activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      const {
        items: newItems,
        total: newTotal,
        page: responsePage,
        perPage,
      } = requestState.data.reportRatingsList;
      setTotal(newTotal);
      setItems(prev =>
        responsePage === 1 ? newItems : [...prev, ...newItems],
      );
      setHasMore(newItems.length >= perPage);
    }
  }, [requestState]);

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    load(nextPage, activeFilters);
  }, [page, activeFilters, load]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    clearTimeout(emailDebounceRef.current!);
    emailDebounceRef.current = setTimeout(() => setEmailFilter(val), 500);
  };

  const handleReportIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    clearTimeout(reportIdDebounceRef.current!);
    reportIdDebounceRef.current = setTimeout(() => setReportIdFilter(val), 500);
  };

  const isLoading =
    requestState.type === 'REQUEST_START' ||
    requestState.type === 'REQUEST_INIT';
  const isEmpty = !isLoading && items.length === 0;

  return (
    <PageWrapper>
      <PageHeader>
        <TrueImpactText variant="heading-page" className="rating-heading">
          Report Ratings
        </TrueImpactText>
        {total > 0 && (
          <span className="total-badge">
            <TrueImpactText variant="body-small-bold" color="ctaPrimary">
              {total.toLocaleString()} total
            </TrueImpactText>
          </span>
        )}
      </PageHeader>

      <FiltersBar>
        <input
          type="text"
          placeholder="Search by user email..."
          onChange={handleEmailChange}
        />
        <input
          type="number"
          placeholder="Report ID..."
          onChange={handleReportIdChange}
          style={{ maxWidth: 160, flex: 'unset' }}
        />
        <select
          value={ratingFilter}
          onChange={e => setRatingFilter(e.target.value)}
        >
          <option value="">⭐ All Ratings</option>
          {[1, 2, 3, 4, 5].map(n => (
            <option key={n} value={n}>
              {'⭐'.repeat(n)} {n} Star{n > 1 ? 's' : ''}
            </option>
          ))}
        </select>
        <select
          value={hasClonedFromIdFilter}
          onChange={e => setHasClonedFromIdFilter(e.target.value)}
        >
          <option value="">All Reports</option>
          <option value="true">All Cloned Reports</option>
          <option value="false">All From Scratch</option>
        </select>
        <select
          value={cnOptedInFilter}
          onChange={e => setCnOptedInFilter(e.target.value)}
        >
          <option value="">All Platforms</option>
          <option value="true">Charity Navigator</option>
          <option value="false">True Impact</option>
        </select>
      </FiltersBar>

      <ContentArea>
        {isLoading && items.length === 0 ? (
          <LoadingWrapper>
            <TrueImpactLoading />
          </LoadingWrapper>
        ) : isEmpty ? (
          <EmptyState>
            <span className="emoji">⭐</span>
            <TrueImpactText>
              No ratings found matching your filters.
            </TrueImpactText>
          </EmptyState>
        ) : (
          <CardsGrid
            style={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}
          >
            {items.map(item => (
              <RatingCard key={item.id} item={item} />
            ))}
          </CardsGrid>
        )}
      </ContentArea>

      {hasMore && !isEmpty && (
        <LoadMore
          loadMore={handleLoadMore}
          options={{ rootMargin: '120px 0px' }}
        >
          <LoadingWrapper>
            <TrueImpactLoading />
          </LoadingWrapper>
        </LoadMore>
      )}
    </PageWrapper>
  );
};

export default ReportRatings;
